import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, Mail, CheckCircle, AlertCircle, Send, Users, Calendar, Eye, X, TestTube, Settings, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import Header from '@/components/Header';
import SendingProgressModal from '@/components/SendingProgressModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RenewalReminders = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchParams, setSearchParams] = useState({
    magazine: 'Aabkari Times',
    status: 'ACTIVE',
    expiry_period: '30_days'
  });
  
  // Sender accounts state
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  
  // Preview Modal State
  const [showPreview, setShowPreview] = useState(false);
  const [previewMember, setPreviewMember] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Progress State
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0, isActive: false });
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressJobId, setProgressJobId] = useState('');

  // Fetch sender accounts when magazine changes
  useEffect(() => {
    fetchSenderAccounts();
  }, [searchParams.magazine]);

  const fetchSenderAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/sender-accounts/${searchParams.magazine}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSenderAccounts(response.data || []);
      
      // Set default sender if available
      const defaultSender = response.data?.find(a => a.is_default);
      if (defaultSender) {
        setSelectedSender(defaultSender);
      } else if (response.data?.length > 0) {
        setSelectedSender(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching sender accounts:', error);
    }
  };

  const handleSenderChange = (accountId) => {
    const account = senderAccounts.find(a => a.id === accountId);
    if (account) {
      setSelectedSender(account);
    }
  };

  const expiryPeriodOptions = [
    { value: '3_days', label: 'Expiring in 3 Days', color: '#dc2626' },
    { value: '7_days', label: 'Expiring in 7 Days', color: '#ea580c' },
    { value: '15_days', label: 'Expiring in 15 Days', color: '#ca8a04' },
    { value: '30_days', label: 'Expiring in 30 Days', color: '#16a34a' },
    { value: 'expired', label: 'Already Expired', color: '#7c3aed' },
    { value: 'all_expiring', label: 'All Expiring (0-30 days)', color: '#2563eb' }
  ];

  const handleSearch = async () => {
    setLoading(true);
    setSelectedMembers([]);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/members/search-renewal`, searchParams, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMembers(response.data.members);
      if (response.data.total_count === 0) {
        toast.info('No members found matching the criteria');
      } else {
        toast.success(`Found ${response.data.total_count} members`);
      }
    } catch (error) {
      console.error('Error searching members:', error);
      toast.error(error.response?.data?.detail || 'Failed to search members');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const membersWithEmail = members.filter(m => m.email && m.email.trim());
      setSelectedMembers(membersWithEmail.map(m => m.member_id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSelectMember = (memberId, checked) => {
    if (checked) {
      setSelectedMembers([...selectedMembers, memberId]);
    } else {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    }
  };

  // Open preview modal before sending
  const handleOpenPreview = () => {
    if (selectedMembers.length === 0) {
      toast.error('Please select at least one member');
      return;
    }
    
    // Get first selected member for preview
    const firstMember = members.find(m => m.member_id === selectedMembers[0]);
    setPreviewMember(firstMember);
    setShowPreview(true);
  };

  // Send test email
  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    if (!previewMember) {
      toast.error('No member selected for preview');
      return;
    }

    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email/send-renewal-test`, {
        magazine: searchParams.magazine,
        member_id: previewMember.member_id,
        test_email: testEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // Confirm and send reminders
  const handleConfirmSend = async () => {
    setShowPreview(false);
    setSending(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Start progress tracking
      const progressResponse = await axios.post(`${API}/email/progress/start`, {
        total: selectedMembers.length,
        email_type: 'renewal_reminder'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jobId = progressResponse.data.job_id;
      setProgressJobId(jobId);
      setShowProgressModal(true);
      
      const response = await axios.post(`${API}/email/send-renewal-reminders`, {
        magazine: searchParams.magazine,
        member_ids: selectedMembers,
        sender_email: selectedSender?.email || '',
        sender_name: selectedSender?.display_name || '',
        job_id: jobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.sent_count} emails sent successfully! ${response.data.failed_count > 0 ? `(${response.data.failed_count} failed)` : ''}`);
      setSelectedMembers([]);
      handleSearch();
      
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
      setShowProgressModal(false);
    } finally {
      setSending(false);
    }
  };

  const handleCancelSend = () => {
    setShowPreview(false);
    toast.info('Email sending cancelled');
  };

  const getExpiryColor = (days) => {
    if (days < 0) return '#7c3aed';
    if (days <= 3) return '#dc2626';
    if (days <= 7) return '#ea580c';
    if (days <= 15) return '#ca8a04';
    return '#16a34a';
  };

  const getExpiryLabel = (days) => {
    if (days < 0) return `Expired ${Math.abs(days)} days ago`;
    if (days === 0) return 'Expires Today!';
    if (days === 1) return 'Expires Tomorrow!';
    return `Expires in ${days} days`;
  };

  const membersWithEmail = members.filter(m => m.email && m.email.trim());
  const allSelected = membersWithEmail.length > 0 && selectedMembers.length === membersWithEmail.length;

  // Generate preview HTML
  const getPreviewHtml = () => {
    if (!previewMember) return '';
    
    const member = previewMember;
    const magazine = searchParams.magazine;
    
    if (magazine === "Aabkari Times" || magazine === "Aabkari Times ENG") {
      return `
        <div style="font-family: Arial, sans-serif; max-width: 100%; background: #ffffff; font-size: 12px; border: 1px solid #e5e7eb;">
          <!-- Header with Logo -->
          <div style="padding: 15px; text-align: center; border-bottom: 2px solid #d4a636;">
            <img src="https://customer-assets.emergentagent.com/job_email-tracking/artifacts/boz6gkkd_logo1.png" alt="Aabkari Times" style="max-height: 60px; max-width: 220px;">
          </div>
          
          <!-- Title -->
          <div style="background: #1e3a5f; padding: 10px; text-align: center;">
            <h3 style="color: #ffffff; margin: 0; font-size: 14px; font-weight: normal;">RENEWAL REMINDER</h3>
          </div>
          
          <!-- Content -->
          <div style="padding: 15px;">
            <p style="font-size: 12px; margin-bottom: 10px;">Dear Sir/Madam,</p>
            <p style="font-size: 11px; line-height: 1.5; margin-bottom: 10px;">Warm greetings from Aabkari Times Magazine!</p>
            <p style="font-size: 11px; line-height: 1.5; margin-bottom: 10px;">
              We are writing to remind you about the renewal of your subscription with reference <strong>Membership No. ${member.member_id}</strong>
            </p>
            <p style="font-size: 11px; line-height: 1.5; margin-bottom: 12px;">
              Your subscription expired on <strong style="color: #dc2626;">${member.expiry_date}</strong>, and we kindly request you to renew it at your earliest convenience.
            </p>
            
            <!-- Tariff Table -->
            <p style="font-size: 11px; font-weight: bold; margin-bottom: 8px;">Renewal Tariffs:</p>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 10px;">
              <tr style="background: #f3f4f6;">
                <th style="padding: 6px; border: 1px solid #d1d5db; text-align: left;">Duration</th>
                <th style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Courier</th>
                <th style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">e-Magazine</th>
              </tr>
              <tr><td style="padding: 6px; border: 1px solid #d1d5db;">1 Year</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 2,000</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 1,000</td></tr>
              <tr><td style="padding: 6px; border: 1px solid #d1d5db;">2 Years</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 3,600</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 1,600</td></tr>
              <tr><td style="padding: 6px; border: 1px solid #d1d5db;">3 Years</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 5,000</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 2,000</td></tr>
              <tr><td style="padding: 6px; border: 1px solid #d1d5db;">Life</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 15,000</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 8,000</td></tr>
            </table>
            
            <!-- QR Code -->
            <div style="text-align: center; margin: 12px 0; padding: 10px; background: #f9fafb; border-radius: 6px;">
              <p style="font-size: 11px; font-weight: bold; margin-bottom: 8px;">Scan to Pay:</p>
              <img src="https://customer-assets.emergentagent.com/job_email-tracking/artifacts/b3qgj695_unnamed.jpg" alt="QR Code" style="max-width: 120px; border: 1px solid #e5e7eb;">
              <p style="font-size: 10px; color: #6b7280; margin-top: 6px;">GPay/UPI: 7379700003@okbizaxis</p>
            </div>
            
            <!-- Bank Details -->
            <p style="font-size: 11px; font-weight: bold; margin-bottom: 6px;">Bank Details (NEFT/Cheque):</p>
            <p style="font-size: 10px; margin: 2px 0;">Bank: Bank of Baroda | A/C: 70020200000558 | IFSC: BARB0VJBELI</p>
            
            <!-- Signature -->
            <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
              <p style="font-size: 11px; margin: 0;">Thanks & Best Regards,</p>
              <p style="font-size: 11px; margin: 3px 0;"><strong>Arvind Dubey</strong> (Business Manager)</p>
            </div>
          </div>
          
          <!-- Footer -->
          <div style="background: #1e3a5f; color: #ffffff; padding: 12px; font-size: 9px;">
            <p style="margin: 0;"><strong>Aabkari Times Magazine</strong> | Monthly News Magazine on Excise and Alco Bev</p>
            <p style="margin: 4px 0;">M: (+91) 7379700003 | E: info@aabkaritimes.com</p>
            <p style="margin: 0; color: #cbd5e1;">Bhawani Bhawan, 485 Mumfordganj, Opp. Shivaji Park, Prayagraj- 211002 (U.P.)</p>
          </div>
        </div>
      `;
    } else {
      return `
        <div style="font-family: Arial, sans-serif; max-width: 100%; background: #ffffff; font-size: 12px; border: 1px solid #e5e7eb;">
          <!-- Header with Logo -->
          <div style="padding: 15px; text-align: center; border-bottom: 2px solid #4ade80;">
            <img src="https://customer-assets.emergentagent.com/job_email-tracking/artifacts/cwvpitgb_sugar%20times%20logo.jpg" alt="Sugar Times" style="max-height: 60px; max-width: 220px;">
          </div>
          
          <!-- Title -->
          <div style="background: #166534; padding: 10px; text-align: center;">
            <h3 style="color: #ffffff; margin: 0; font-size: 14px; font-weight: normal;">RENEWAL REMINDER</h3>
          </div>
          
          <!-- Content -->
          <div style="padding: 15px;">
            <p style="font-size: 12px; margin-bottom: 10px;">Dear Sir,</p>
            <p style="font-size: 11px; line-height: 1.5; margin-bottom: 10px;">Greetings!</p>
            <p style="font-size: 11px; line-height: 1.5; margin-bottom: 10px;">
              This is a reminder for the renewal of your Sugar Times Magazine Membership with reference <strong>ID: ${member.member_id}</strong>
            </p>
            <p style="font-size: 11px; line-height: 1.5; margin-bottom: 12px;">
              Your subscription expired on <strong style="color: #dc2626;">${member.expiry_date}</strong>. We kindly request you to renew it at your earliest convenience.
            </p>
            
            <!-- Tariff Table -->
            <p style="font-size: 11px; font-weight: bold; margin-bottom: 8px;">Renewal Tariff:</p>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 10px;">
              <tr style="background: #dcfce7;">
                <th style="padding: 6px; border: 1px solid #bbf7d0; text-align: left;">Duration</th>
                <th style="padding: 6px; border: 1px solid #bbf7d0; text-align: center;">Courier</th>
                <th style="padding: 6px; border: 1px solid #bbf7d0; text-align: center;">e-Magazine</th>
              </tr>
              <tr><td style="padding: 6px; border: 1px solid #d1d5db;">1 Year</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 2,000</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 1,000</td></tr>
              <tr><td style="padding: 6px; border: 1px solid #d1d5db;">2 Years</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 3,600</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 1,600</td></tr>
              <tr><td style="padding: 6px; border: 1px solid #d1d5db;">3 Years</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 5,000</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 2,000</td></tr>
              <tr><td style="padding: 6px; border: 1px solid #d1d5db;">Life</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 15,000</td><td style="padding: 6px; border: 1px solid #d1d5db; text-align: center;">Rs. 8,000</td></tr>
            </table>
            
            <!-- Online Renewal -->
            <p style="font-size: 10px; margin-bottom: 8px;">
              <strong>Online Renewal:</strong> <a href="https://rzp.io/l/rIAjUv0" style="color: #166534;">https://rzp.io/l/rIAjUv0</a>
            </p>
            
            <!-- QR Code -->
            <div style="text-align: center; margin: 12px 0; padding: 10px; background: #f0fdf4; border-radius: 6px;">
              <p style="font-size: 11px; font-weight: bold; margin-bottom: 8px;">Scan to Pay:</p>
              <img src="https://customer-assets.emergentagent.com/job_email-tracking/artifacts/fjtp0nd8_Sugar%20times%20qr.jpeg" alt="QR Code" style="max-width: 120px; border: 1px solid #e5e7eb;">
              <p style="font-size: 10px; color: #6b7280; margin-top: 6px;">UPI ID: 8756062435@okbizaxis</p>
            </div>
            
            <!-- Bank Details -->
            <p style="font-size: 11px; font-weight: bold; margin-bottom: 6px;">Bank Details (NEFT/Cheque):</p>
            <p style="font-size: 10px; margin: 2px 0;">Bank: UCO Bank | A/C: 16110210000861 | IFSC: UCBA0001611</p>
            
            <!-- Signature -->
            <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
              <p style="font-size: 11px; margin: 0;">Thanks & Best Regards,</p>
              <p style="font-size: 11px; margin: 3px 0;"><strong>Shashi Yogeshwar</strong> (Business Manager)</p>
            </div>
          </div>
          
          <!-- Footer -->
          <div style="background: #166534; color: #ffffff; padding: 12px; font-size: 9px;">
            <p style="margin: 0;"><strong>Sugar Times Magazine</strong> | Monthly News Magazine on Sugarcane and Ethanol Industry</p>
            <p style="margin: 4px 0;">T: (+91) 73554 53462 | E: info@sugartimes.co.in</p>
            <p style="margin: 0; color: #bbf7d0;">Bhawani Bhawan, 485 Mumfordganj, Opp. Shivaji Park, Prayagraj- 211002 (U.P.)</p>
          </div>
        </div>
      `;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              Subscription Renewal Reminders
            </h1>
          </div>
        </div>

        {/* Progress Bar */}
        {sendProgress.isActive && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-blue-800">Sending Emails...</span>
              <span className="text-blue-600">{sendProgress.current} / {sendProgress.total}</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-4">
              <div 
                className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                style={{ width: `${(sendProgress.current / sendProgress.total) * 100}%` }}
              ></div>
            </div>
            <p className="text-sm text-blue-600 mt-2">
              {Math.round((sendProgress.current / sendProgress.total) * 100)}% Complete
            </p>
          </div>
        )}

        {/* Search Filters */}
        <div className="form-section mb-8">
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#7c3aed' }}>
            <Search className="inline mr-2" size={24} />
            Search Members
          </h2>
          
          <div className="grid grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <select
                value={searchParams.magazine}
                onChange={(e) => setSearchParams({ ...searchParams, magazine: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="Aabkari Times">Aabkari Times</option>
                <option value="Sugar Times">Sugar Times</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Member Status</Label>
              <select
                value={searchParams.status}
                onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="">All</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Expiry Period</Label>
              <select
                value={searchParams.expiry_period}
                onChange={(e) => setSearchParams({ ...searchParams, expiry_period: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {expiryPeriodOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button 
                onClick={handleSearch} 
                disabled={loading}
                className="w-full"
                style={{ backgroundColor: '#7c3aed' }}
              >
                <Search className="mr-2" size={18} />
                {loading ? 'Searching...' : 'Search Members'}
              </Button>
            </div>
          </div>
        </div>

        {/* Sender Selection - Only show after search has results */}
        {members.length > 0 && (
          <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Label className="font-semibold text-indigo-800">Send From:</Label>
                <select
                  value={selectedSender?.id || ''}
                  onChange={(e) => handleSenderChange(e.target.value)}
                  className="h-10 w-72 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm"
                >
                  {senderAccounts.length === 0 ? (
                    <option value="">No sender accounts configured</option>
                  ) : (
                    senderAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.display_name} ({account.email})
                      </option>
                    ))
                  )}
                </select>
              </div>
              {senderAccounts.length === 0 && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => navigate('/email-settings')}
                  className="text-indigo-700 border-indigo-300"
                >
                  <Settings className="mr-1" size={14} />
                  Configure Senders
                </Button>
              )}
            </div>
            {selectedSender && (
              <p className="text-xs text-indigo-600 mt-2">
                Renewal reminders will be sent from: <strong>{selectedSender.email}</strong>
              </p>
            )}
          </div>
        )}

        {/* Results Summary */}
        {members.length > 0 && (
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-3">
                <Users className="text-blue-600" size={24} />
                <div>
                  <p className="text-2xl font-bold text-blue-800">{members.length}</p>
                  <p className="text-sm text-blue-600">Total Members Found</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center space-x-3">
                <Mail className="text-green-600" size={24} />
                <div>
                  <p className="text-2xl font-bold text-green-800">{membersWithEmail.length}</p>
                  <p className="text-sm text-green-600">With Valid Email</p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-purple-600" size={24} />
                <div>
                  <p className="text-2xl font-bold text-purple-800">{selectedMembers.length}</p>
                  <p className="text-sm text-purple-600">Selected for Email</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Bar */}
        {members.length > 0 && (
          <div className="bg-gray-100 p-4 rounded-lg mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAll}
                id="select-all"
              />
              <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                Select All with Email ({membersWithEmail.length})
              </label>
            </div>
            
            <Button 
              onClick={handleOpenPreview} 
              disabled={sending || selectedMembers.length === 0}
              style={{ backgroundColor: '#059669' }}
            >
              <Eye className="mr-2" size={18} />
              Preview & Send ({selectedMembers.length})
            </Button>
          </div>
        )}

        {/* Members Table */}
        {members.length > 0 && (
          <div className="form-section">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3 text-left w-12"></th>
                    <th className="p-3 text-left">Member ID</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Company</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Expiry Date</th>
                    <th className="p-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member, index) => {
                    const hasEmail = member.email && member.email.trim();
                    const expiryColor = getExpiryColor(member.days_until_expiry);
                    
                    return (
                      <tr 
                        key={member.member_id} 
                        className={`border-b hover:bg-gray-50 ${!hasEmail ? 'bg-red-50' : ''}`}
                      >
                        <td className="p-3">
                          {hasEmail ? (
                            <Checkbox
                              checked={selectedMembers.includes(member.member_id)}
                              onCheckedChange={(checked) => handleSelectMember(member.member_id, checked)}
                            />
                          ) : (
                            <span title="No email address" className="text-red-400">
                              <AlertCircle size={18} />
                            </span>
                          )}
                        </td>
                        <td className="p-3 font-medium">{member.member_id}</td>
                        <td className="p-3">{member.name}</td>
                        <td className="p-3">{member.company_name}</td>
                        <td className="p-3">
                          {hasEmail ? (
                            <span className="text-green-600">{member.email}</span>
                          ) : (
                            <span className="text-red-500 italic">No email</span>
                          )}
                        </td>
                        <td className="p-3">{member.expiry_date}</td>
                        <td className="p-3">
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: expiryColor }}
                          >
                            {getExpiryLabel(member.days_until_expiry)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && members.length === 0 && (
          <div className="form-section text-center py-12">
            <Calendar className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Members Found</h3>
            <p className="text-gray-500">
              Use the filters above to search for members whose subscriptions are expiring.
            </p>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <Eye className="mr-2" size={24} />
                  Email Preview - Review Before Sending
                </h2>
                <button onClick={handleCancelSend} className="text-white hover:text-gray-200">
                  <X size={24} />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Summary */}
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <h3 className="font-bold text-blue-800 mb-2">📧 Email Summary</h3>
                  <p className="text-sm text-blue-700">
                    <strong>Recipients:</strong> {selectedMembers.length} members<br/>
                    <strong>Magazine:</strong> {searchParams.magazine}<br/>
                    <strong>Subject:</strong> Request to Renew: Your Member ID {'{MemberID}'} Expires on {'{ExpiryDate}'}
                  </p>
                </div>

                {/* Test Email Section */}
                <div className="bg-yellow-50 p-4 rounded-lg mb-6 border border-yellow-200">
                  <h3 className="font-bold text-yellow-800 mb-3 flex items-center">
                    <TestTube className="mr-2" size={20} />
                    Send Test Email First (Recommended)
                  </h3>
                  <p className="text-sm text-yellow-700 mb-3">
                    Send a test email to yourself to verify the content before sending to all {selectedMembers.length} members.
                  </p>
                  <div className="flex space-x-3">
                    <Input 
                      type="email"
                      placeholder="Enter your email address"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleSendTestEmail}
                      disabled={sendingTest || !testEmail}
                      variant="outline"
                      className="border-yellow-400 text-yellow-700 hover:bg-yellow-100"
                    >
                      <Send className="mr-2" size={16} />
                      {sendingTest ? 'Sending...' : 'Send Test'}
                    </Button>
                  </div>
                </div>
                
                {/* Preview Content */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-3 border-b">
                    <p className="text-sm font-medium text-gray-600">Email Preview (Sample: {previewMember?.name})</p>
                  </div>
                  <div 
                    className="p-4 bg-white"
                    dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                  />
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="bg-gray-100 p-4 flex justify-between items-center border-t">
                <Button 
                  variant="outline" 
                  onClick={handleCancelSend}
                  className="text-gray-600"
                >
                  <X className="mr-2" size={18} />
                  Cancel
                </Button>
                <div className="flex space-x-3">
                  <p className="text-sm text-gray-500 self-center">
                    Ready to send to <strong>{selectedMembers.length}</strong> members?
                  </p>
                  <Button 
                    onClick={handleConfirmSend}
                    disabled={sending}
                    style={{ backgroundColor: '#059669' }}
                  >
                    <Send className="mr-2" size={18} />
                    {sending ? 'Sending...' : `Confirm & Send (${selectedMembers.length})`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Sending Progress Modal */}
        <SendingProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          jobId={progressJobId}
          totalRecipients={selectedMembers.length}
          emailType="Renewal Reminder"
        />
      </div>
    </div>
  );
};

export default RenewalReminders;
